import React, { useState } from 'react';
import { Button, Card, Col, message, Popconfirm, Row, Select, Space, Table, TableColumnsType, Tabs } from 'antd';
import type { TableRowSelection } from 'antd/es/table/interface';
import {
  getSkillToolBindings,
  bindToolToSkill,
  unbindToolFromSkill,
  SkillToolBindingVo,
  McpToolVo,
  SkillToolBindingReq,
} from '@/api/skillToolBinding';
import { McpServerConfigVo } from '@/api/mcpServer';
import { SkillVersionVo, SkillRegistryVo, switchSkillVersion, deleteSkillVersion } from '@/api/skill';
import { PlusOutlined, ApiOutlined, DeleteOutlined, ToolOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

interface SkillExpandRowProps {
  skill: SkillRegistryVo;
  versionList: SkillVersionVo[];
  toolBindings: SkillToolBindingVo[];
  availableTools?: { mcpTools: McpToolVo[]; totalMcpTools: number };
  mcpServerList?: McpServerConfigVo[];
  onAddVersion: (skillId: number) => void;
  onEditVersion: (skillId: number, version: SkillVersionVo) => void;
  onSwitchVersion: (skillId: number, versionId: number) => void;
  onDeleteVersion: (skillId: number, versionId: number) => void;
  onToolBindingsChange: (skillId: number, bindings: SkillToolBindingVo[]) => void;
}

const versionColumns = [
  { title: '版本号', dataIndex: 'version', key: 'version', width: 100 },
  { title: '变更说明', dataIndex: 'changeLog', key: 'changeLog', ellipsis: true },
  { title: '创建人', dataIndex: 'createdBy', key: 'createdBy', width: 100 },
  { title: '创建时间', dataIndex: 'createdAt', key: 'createdAt', width: 160,
    render: text => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-' },
  { title: '更新人', dataIndex: 'updatedBy', key: 'updatedBy', width: 100 },
  { title: '更新时间', dataIndex: 'updatedAt', key: 'updatedAt', width: 160,
    render: text => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-' },
];

const SkillExpandRow: React.FC<SkillExpandRowProps> = ({
  skill,
  versionList,
  toolBindings,
  availableTools,
  mcpServerList,
  onAddVersion,
  onEditVersion,
  onSwitchVersion,
  onDeleteVersion,
  onToolBindingsChange,
}) => {
  const [addingToolCodes, setAddingToolCodes] = useState<string[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  // 版本列表列定义
  const versionActionColumns = [
    ...versionColumns,
    {
      title: '操作',
      key: 'operation',
      width: 150,
      render: (_: any, record: SkillVersionVo) => (
        <Space size={4}>
          <Button type="link" size="small" style={{ padding: 0 }}
            onClick={() => onEditVersion(skill.id, record)}>编辑</Button>
          <Button type="link" size="small" style={{ padding: 0 }}
            onClick={() => onSwitchVersion(skill.id, record.id)}>应用</Button>
          <Popconfirm title="确定删除该版本？" onConfirm={() => onDeleteVersion(skill.id, record.id)}>
            <Button type="link" size="small" style={{ padding: 0 }} danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 绑定工具（仅 MCP）
  const handleBind = () => {
    if (addingToolCodes.length === 0) {
      message.warning('请选择要绑定的工具');
      return;
    }

    const requests: SkillToolBindingReq[] = addingToolCodes.map(toolCode => {
      const mcpTool = availableTools?.mcpTools?.find(t => t.toolName === toolCode);
      return {
        skillId: skill.id,
        toolCode,
        toolId: mcpTool?.id,
        toolSource: 'mcp',
        toolName: mcpTool?.displayName || mcpTool?.toolName,
        toolDescription: mcpTool?.description,
      };
    });

    Promise.all(requests.map(req => bindToolToSkill(req))).then(() => {
      message.success(`成功绑定 ${addingToolCodes.length} 个工具`);
      setAddingToolCodes([]);
      getSkillToolBindings(skill.id).then(list => onToolBindingsChange(skill.id, list || []));
    });
  };

  // 解绑
  const handleUnbind = (toolCode: string) => {
    unbindToolFromSkill({ skillId: skill.id, toolCode }).then(() => {
      message.success('解绑成功');
      getSkillToolBindings(skill.id).then(list => onToolBindingsChange(skill.id, list || []));
    });
  };

  // 批量解绑
  const handleBatchUnbind = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要解绑的工具');
      return;
    }
    const toolCodes = toolBindings
      .filter(b => selectedRowKeys.includes(b.id))
      .map(b => b.toolCode);

    Promise.all(toolCodes.map(toolCode => unbindToolFromSkill({ skillId: skill.id, toolCode })))
      .then(() => {
        message.success(`成功解绑 ${toolCodes.length} 个工具`);
        setSelectedRowKeys([]);
        getSkillToolBindings(skill.id).then(list => onToolBindingsChange(skill.id, list || []));
      });
  };

  // 表格多选配置
  const rowSelection: TableRowSelection<SkillToolBindingVo> = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  // 获取绑定记录的 MCP Server 名称
  const getBindingMcpServerName = (binding: SkillToolBindingVo) => {
    const mcpTool = availableTools?.mcpTools?.find(t => t.toolName === binding.toolCode);
    if (!mcpTool?.mcpServerId) return '-';
    const server = mcpServerList?.find(s => s.id === mcpTool.mcpServerId);
    return server?.displayName || server?.serverName || '-';
  };

  // 绑定列表列定义（删除来源列）
  const bindingColumns: TableColumnsType<SkillToolBindingVo> = [
    { title: '工具编码', dataIndex: 'toolCode', key: 'toolCode', width: 150 },
    { title: '工具名称', dataIndex: 'toolName', key: 'toolName', width: 120,
      render: (name: string, record: SkillToolBindingVo) => name || record.toolCode },
    { title: 'MCP Server', key: 'server', width: 100, render: (_: any, record: SkillToolBindingVo) => getBindingMcpServerName(record) },
    { title: '描述', dataIndex: 'toolDescription', key: 'toolDescription', ellipsis: true },
    { title: '操作', key: 'operation', width: 50,
      render: (_: any, record: SkillToolBindingVo) => (
        <Popconfirm title="确定解绑该工具？" onConfirm={() => handleUnbind(record.toolCode)}>
          <Button type="link" size="small" style={{ padding: 0 }} danger>解绑</Button>
        </Popconfirm>
      )},
  ];

  // 过滤已绑定的工具（仅 MCP）
  const unboundMcpTools = availableTools?.mcpTools && toolBindings
    ? availableTools.mcpTools.filter(t => !toolBindings.map(b => b.toolCode).includes(t.toolName))
    : availableTools?.mcpTools || [];

  // MCP 工具按 Server 排序并计算合并行
  const sortedMcpTools = [...unboundMcpTools].sort((a, b) => (a.mcpServerId || 0) - (b.mcpServerId || 0));

  const getMcpServerRowSpan = (record: McpToolVo, index: number) => {
    const serverId = record.mcpServerId || 0;
    const firstIndex = sortedMcpTools.findIndex(t => (t.mcpServerId || 0) === serverId);
    if (firstIndex !== index) return 0;
    return sortedMcpTools.filter(t => (t.mcpServerId || 0) === serverId).length;
  };

  const mcpToolColumns: TableColumnsType<McpToolVo> = [
    {
      title: 'MCP Server',
      dataIndex: 'mcpServerId',
      key: 'mcpServerId',
      width: 100,
      onCell: (record, index) => ({ rowSpan: getMcpServerRowSpan(record, index ?? 0) }),
      render: (serverId: number) => {
        const server = mcpServerList?.find(s => s.id === serverId);
        return server?.displayName || server?.serverName || '-';
      },
    },
    { title: '工具名称', dataIndex: 'toolName', key: 'toolName', width: 150 },
    { title: '显示名称', dataIndex: 'displayName', key: 'displayName', width: 100 },
    { title: '描述', dataIndex: 'description', key: 'description', ellipsis: true },
  ];

  // MCP 工具按 Server 分组
  const mcpServerGroups = mcpServerList && unboundMcpTools.length
    ? (() => {
        const groups: Map<number, { label: string; options: { label: string; value: string }[] }> = new Map();
        unboundMcpTools.forEach(tool => {
          const serverId = tool.mcpServerId || 0;
          const server = mcpServerList.find(s => s.id === serverId);
          const serverName = server?.displayName || server?.serverName || '未知服务';
          if (!groups.has(serverId)) groups.set(serverId, { label: serverName, options: [] });
          groups.get(serverId)!.options.push({ label: tool.displayName || tool.toolName, value: tool.toolName });
        });
        return Array.from(groups.values());
      })()
    : [];

  const handleSelectServerTools = (serverId: number) => {
    const serverTools = unboundMcpTools.filter(t => (t.mcpServerId || 0) === serverId);
    const serverToolCodes = serverTools.map(t => t.toolName);
    const isServerSelected = serverToolCodes.every(code => addingToolCodes.includes(code));
    if (isServerSelected) {
      setAddingToolCodes(addingToolCodes.filter(code => !serverToolCodes.includes(code)));
    } else {
      setAddingToolCodes([...addingToolCodes, ...serverToolCodes.filter(code => !addingToolCodes.includes(code))]);
    }
  };

  return (
    <div style={{ padding: '12px 24px', background: '#fafafa' }}>
      <Tabs defaultActiveKey="tools" items={[
        {
          key: 'tools',
          label: <><ToolOutlined /> 工具绑定 ({toolBindings.length})</>,
          children: (
            <Row gutter={16}>
              {/* 左侧：已绑定工具 */}
              <Col span={12}>
                <Card title={<Space><ApiOutlined /> 已绑定工具 ({toolBindings.length})</Space>} size="small">
                  {/* 添加工具（仅 MCP） */}
                  <div style={{ marginBottom: 12 }}>
                    <Space wrap>
                      <Select mode="multiple" value={addingToolCodes} onChange={setAddingToolCodes} style={{ width: 320 }}
                        options={[]} placeholder="选择 MCP 工具（按服务分组）" maxTagCount="responsive"
                        dropdownRender={() => <>
                          {mcpServerGroups.map(group => {
                            const server = mcpServerList?.find(s => (s.displayName || s.serverName) === group.label);
                            const serverId = server?.id || 0;
                            const serverTools = unboundMcpTools.filter(t => (t.mcpServerId || 0) === serverId);
                            const isServerSelected = serverTools.length > 0 && serverTools.every(t => addingToolCodes.includes(t.toolName));
                            return (
                              <div key={serverId} style={{ padding: '4px 12px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                  padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
                                  <span style={{ fontWeight: 500, color: '#666' }}>{group.label}</span>
                                  <Button type="link" size="small" onClick={() => handleSelectServerTools(serverId)}>
                                    {isServerSelected ? '取消' : '全选'}
                                  </Button>
                                </div>
                                <div style={{ padding: '4px 0' }}>
                                  {group.options.map(opt => (
                                    <div key={opt.value} style={{ padding: '4px 8px', cursor: 'pointer',
                                      background: addingToolCodes.includes(opt.value) ? '#e6f7ff' : 'transparent' }}
                                      onClick={() => setAddingToolCodes(addingToolCodes.includes(opt.value)
                                        ? addingToolCodes.filter(c => c !== opt.value) : [...addingToolCodes, opt.value])}>
                                      {opt.label}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </>}/>
                      <Button type="primary" icon={<PlusOutlined />} onClick={handleBind}>绑定</Button>
                    </Space>
                  </div>
                  {/* 批量解绑 */}
                  {selectedRowKeys.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <Popconfirm title={`确定解绑选中的 ${selectedRowKeys.length} 个工具？`} onConfirm={handleBatchUnbind}>
                        <Button danger icon={<DeleteOutlined />} size="small">
                          批量解绑 ({selectedRowKeys.length})
                        </Button>
                      </Popconfirm>
                    </div>
                  )}
                  <Table<SkillToolBindingVo> columns={bindingColumns} dataSource={toolBindings}
                    rowKey="id" size="small" pagination={false} rowSelection={rowSelection}/>
                </Card>
              </Col>

              {/* 右侧：可用工具（仅 MCP） */}
              <Col span={12}>
                <Card title={<Space><ApiOutlined /> 可用 MCP 工具 ({unboundMcpTools.length})</Space>} size="small">
                  <Table<McpToolVo> columns={mcpToolColumns} dataSource={sortedMcpTools}
                    rowKey="id" size="small" pagination={false}/>
                </Card>
              </Col>
            </Row>
          ),
        },
        {
          key: 'versions',
          label: <><ApiOutlined /> 版本列表 ({versionList.length})</>,
          children: (
            <>
              <div style={{ marginBottom: 12 }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => onAddVersion(skill.id)}>
                  新增版本
                </Button>
              </div>
              <Table<SkillVersionVo> columns={versionActionColumns} dataSource={versionList}
                pagination={false} rowKey="id" size="small"/>
            </>
          ),
        },
      ]}/>
    </div>
  );
};

export default SkillExpandRow;